import isEqual from "fast-deep-equal"

import {
  getScriptTurnKind,
  getScriptTurnText,
  isDtmfScriptTurn,
  type ScriptTurn,
} from "@/types/ScriptTurn"

import type { EditableTestCase, PendingChanges } from "../model"
import {
  validateEditUpdates,
  validateNewTestCases,
} from "./pendingChangesValidation"

export {
  getPendingDiffSummaries,
  getPendingTestCaseIds,
} from "./copilotPendingDiffs"

interface CopilotPendingRowMeta {
  label: string
  pendingIndex: number
  testCaseId: string | null
}

type CopilotDispatchAction =
  | {
      actionType: "EDIT"
      count: number
      updates: Array<{
        id: string
        updates: Partial<EditableTestCase>
      }>
    }
  | {
      actionType: "ADD"
      count: number
      testCases: EditableTestCase[]
    }
  | {
      actionType: "DELETE"
      count: number
      testCaseIds: string[]
    }

function truncateLabel(
  value: string | null | undefined,
  maxLength = 40,
): string | null {
  if (!value) {
    return null
  }

  return value.length > maxLength
    ? `${value.slice(0, maxLength).trimEnd()}...`
    : value
}

function scriptTurnLabel(turn: ScriptTurn | undefined): string | null {
  if (!turn) {
    return null
  }
  const kind = getScriptTurnKind(turn)
  if (kind === "dtmf" && isDtmfScriptTurn(turn)) {
    return `DTMF ${turn.digits || "(empty)"}`
  }
  if (kind === "unknown") {
    return null
  }
  return getScriptTurnText(turn)
}

function getTestCaseLabel(testCase?: EditableTestCase): string {
  return (
    truncateLabel(testCase?.description) ??
    truncateLabel(testCase?.input_str) ??
    truncateLabel(scriptTurnLabel(testCase?.script_turns?.[0])) ??
    "Row ?"
  )
}

function mergeExpectedOutputPatch(
  testCase: EditableTestCase,
  update: Partial<EditableTestCase> & { id: string },
) {
  if (update.expected_output_json === undefined) {
    return update
  }

  const base = testCase.expected_output_json || {}
  const patch = update.expected_output_json || {}
  const nulledKeys = Object.entries(patch)
    .filter(([, value]) => value === null)
    .map(([key]) => key)
  const baseKeys = Object.keys(base)
  const clearsAll =
    baseKeys.length > 0 && baseKeys.every((key) => nulledKeys.includes(key))

  if (clearsAll) {
    return {
      ...update,
      expected_output_json: null,
    }
  }

  const merged = { ...base, ...patch }
  const resolved = Object.fromEntries(
    Object.entries(merged).filter(([, value]) => value !== null),
  )

  return {
    ...update,
    expected_output_json: Object.keys(resolved).length > 0 ? resolved : null,
  }
}

function hasEditChange(
  testCase: EditableTestCase,
  update: Partial<EditableTestCase> & { id: string },
) {
  return (
    (update.input_str !== undefined &&
      update.input_str !== testCase.input_str) ||
    (update.description !== undefined &&
      update.description !== testCase.description) ||
    (update.input_type !== undefined &&
      update.input_type !== testCase.input_type) ||
    (update.expected_behaviors !== undefined &&
      !isEqual(update.expected_behaviors, testCase.expected_behaviors)) ||
    (update.expected_output_json !== undefined &&
      !isEqual(
        update.expected_output_json ?? null,
        testCase.expected_output_json ?? null,
      )) ||
    (update.script_turns !== undefined &&
      !isEqual(update.script_turns ?? null, testCase.script_turns ?? null))
  )
}

export function applyPendingRowAction(input: {
  actionType: string
  currentPendingChanges: PendingChanges | null
  rowData: Record<string, unknown>
  testCases: EditableTestCase[]
}): { pendingChanges: PendingChanges; rowMeta: CopilotPendingRowMeta } | null {
  const { actionType, currentPendingChanges, rowData, testCases } = input

  if (
    (actionType === "EDIT" || actionType === "DELETE") &&
    typeof rowData.id === "string" &&
    !testCases.some((testCase) => testCase.id === rowData.id)
  ) {
    return null
  }

  if (actionType === "EDIT") {
    const result = validateEditUpdates([
      rowData as Parameters<typeof validateEditUpdates>[0][0],
    ])

    if (!result.isValid || !result.data) {
      return null
    }

    const currentEdit = result.data[0]
    const existingTestCase = testCases.find(
      (testCase) => testCase.id === currentEdit.id,
    )
    const existingPendingIndex =
      currentPendingChanges?.type === "EDIT"
        ? currentPendingChanges.data.findIndex(
            (pendingEdit) => pendingEdit.id === currentEdit.id,
          )
        : -1
    const existingPendingEdit =
      existingPendingIndex >= 0 && currentPendingChanges?.type === "EDIT"
        ? currentPendingChanges.data[existingPendingIndex]
        : null
    const comparisonTestCase =
      existingTestCase && existingPendingEdit
        ? { ...existingTestCase, ...existingPendingEdit }
        : existingTestCase
    const mergedEdit = comparisonTestCase
      ? mergeExpectedOutputPatch(comparisonTestCase, currentEdit)
      : currentEdit

    if (comparisonTestCase && !hasEditChange(comparisonTestCase, mergedEdit)) {
      return null
    }

    const nextEdit = existingPendingEdit
      ? { ...existingPendingEdit, ...mergedEdit }
      : mergedEdit
    const nextPendingData =
      currentPendingChanges?.type === "EDIT"
        ? [...currentPendingChanges.data]
        : []

    if (existingPendingIndex >= 0) {
      nextPendingData[existingPendingIndex] = nextEdit
    } else {
      nextPendingData.push(nextEdit)
    }

    const pendingChanges: PendingChanges = {
      type: "EDIT",
      data: nextPendingData,
    }

    return {
      pendingChanges,
      rowMeta: {
        label: getTestCaseLabel(existingTestCase),
        pendingIndex:
          existingPendingIndex >= 0
            ? existingPendingIndex
            : pendingChanges.data.length - 1,
        testCaseId: nextEdit.id,
      },
    }
  }

  if (actionType === "ADD_NEW") {
    const result = validateNewTestCases([
      rowData as Parameters<typeof validateNewTestCases>[0][0],
    ])

    if (!result.isValid || !result.data) {
      return null
    }

    const newTestCase = result.data[0]
    const pendingChanges: PendingChanges = {
      type: "ADD",
      data: [
        ...(currentPendingChanges?.type === "ADD"
          ? currentPendingChanges.data
          : []),
        ...result.data,
      ],
    }

    return {
      pendingChanges,
      rowMeta: {
        label:
          truncateLabel(newTestCase.description) ??
          truncateLabel(newTestCase.input_str) ??
          "New row",
        pendingIndex: pendingChanges.data.length - 1,
        testCaseId: newTestCase.id,
      },
    }
  }

  if (actionType === "DELETE") {
    const id = typeof rowData.id === "string" ? rowData.id : null
    if (!id) {
      return null
    }

    const pendingChanges: PendingChanges = {
      type: "DELETE",
      data: [
        ...(currentPendingChanges?.type === "DELETE"
          ? currentPendingChanges.data
          : []),
        id,
      ],
    }

    return {
      pendingChanges,
      rowMeta: {
        label: getTestCaseLabel(
          testCases.find((testCase) => testCase.id === id),
        ),
        pendingIndex: pendingChanges.data.length - 1,
        testCaseId: id,
      },
    }
  }

  return null
}

function formatEditDispatchUpdate(
  edit: Partial<EditableTestCase> & { id: string },
) {
  return {
    id: edit.id,
    updates: {
      ...(edit.input_str !== undefined && {
        input_str: edit.input_str,
      }),
      ...(edit.description !== undefined && {
        description: edit.description,
      }),
      ...(edit.expected_behaviors !== undefined && {
        expected_behaviors: edit.expected_behaviors,
      }),
      ...(edit.input_type !== undefined && {
        input_type: edit.input_type,
      }),
      ...(edit.expected_output_json !== undefined && {
        expected_output_json: edit.expected_output_json,
      }),
      ...(edit.script_turns !== undefined && {
        script_turns: edit.script_turns,
      }),
    },
  }
}

export function getCopilotDispatchAction(
  pendingChanges: PendingChanges | null,
  index?: number,
): CopilotDispatchAction | null {
  if (!pendingChanges) {
    return null
  }

  if (pendingChanges.type === "EDIT") {
    const edits =
      index === undefined
        ? pendingChanges.data
        : pendingChanges.data[index]
          ? [pendingChanges.data[index]]
          : []

    if (edits.length === 0) {
      return null
    }

    return {
      actionType: "EDIT",
      count: edits.length,
      updates: edits.map(formatEditDispatchUpdate),
    }
  }

  if (pendingChanges.type === "ADD") {
    const testCases =
      index === undefined
        ? pendingChanges.data
        : pendingChanges.data[index]
          ? [pendingChanges.data[index]]
          : []

    if (testCases.length === 0) {
      return null
    }

    return {
      actionType: "ADD",
      count: testCases.length,
      testCases,
    }
  }

  const testCaseIds =
    index === undefined
      ? pendingChanges.data
      : pendingChanges.data[index]
        ? [pendingChanges.data[index]]
        : []

  if (testCaseIds.length === 0) {
    return null
  }

  return {
    actionType: "DELETE",
    count: testCaseIds.length,
    testCaseIds,
  }
}

/**
 * Mirrors the COPILOT_* reducers so a save can build the accepted row list in
 * the same tick it dispatches them, before the editor state has re-rendered.
 */
export function applyCopilotDispatchAction(
  testCases: EditableTestCase[],
  action: CopilotDispatchAction | null,
): EditableTestCase[] {
  if (!action) {
    return testCases
  }

  switch (action.actionType) {
    case "ADD":
      return [...testCases, ...action.testCases]
    case "DELETE": {
      const deletedIds = new Set(action.testCaseIds)
      return testCases.filter((testCase) => !deletedIds.has(testCase.id))
    }
    case "EDIT":
      return testCases.map((testCase) => {
        const update = action.updates.find(
          (candidate) => candidate.id === testCase.id,
        )
        return update ? { ...testCase, ...update.updates } : testCase
      })
  }
}

export function removePendingChangeAtIndex(
  pendingChanges: PendingChanges | null,
  index: number,
): PendingChanges | null {
  if (!pendingChanges) {
    return null
  }

  if (index < 0 || index >= pendingChanges.data.length) {
    return pendingChanges
  }

  const nextData = [...pendingChanges.data]
  nextData.splice(index, 1)

  if (nextData.length === 0) {
    return null
  }

  return {
    type: pendingChanges.type,
    data: nextData,
  } as PendingChanges
}

export function findPendingIndexByTestCaseId(
  pendingChanges: PendingChanges | null,
  testCaseId: string | null,
): number {
  if (!pendingChanges || !testCaseId) {
    return -1
  }

  if (pendingChanges.type === "DELETE") {
    return pendingChanges.data.indexOf(testCaseId)
  }

  return pendingChanges.data.findIndex((entry) => entry.id === testCaseId)
}
